"use client";

/**
 * Authentication client for HIVE
 *
 * Authentication flow:
 * 1. Try local Better Auth (for admin users created during install)
 * 2. If local fails, try mstroHub authentication (for synced creators)
 *
 * Better Auth handles session cookies automatically
 */

import { createAuthClient } from "better-auth/react";
import { _updateCachedToken } from "./auth";

export interface User {
  id: string;
  email: string;
  name?: string;
  avatar_url?: string;
  role?: string;
  organizationId?: string;
  user_metadata?: {
    display_name?: string;
    avatar_url?: string;
    mstrohub_creator_id?: string;
    mstrohub_assignment_id?: string;
    organization_id?: string;
    banned?: boolean;
    [key: string]: any; // Allow other fields from ng_users table
  };
}

export interface Session {
  user: User;
  token?: string;
  activeOrganizationId?: string;
}

// Create Better Auth client pointing to local Next.js API route
const betterAuthClient = createAuthClient({
  baseURL:
    typeof window !== "undefined"
      ? window.location.origin
      : process.env.NEXT_PUBLIC_BETTER_AUTH_URL || "http://localhost:8100",
  fetchOptions: {
    // ensure session cookies are sent on every auth request
    credentials: "include",
  },
});

/**
 * Sign in with email and password
 * Uses Better Auth directly (users are synced from mstroHub into this instance)
 */
export async function signIn(
  email: string,
  password: string
): Promise<{ data?: Session; error?: string }> {
  try {
    const result = await betterAuthClient.signIn.email({
      email,
      password,
    });

    if (result.data) {
      // Cache the session token if available for API calls
      const sessionResult = await betterAuthClient.getSession();
      if (sessionResult.data?.session?.token) {
        _updateCachedToken(sessionResult.data.session.token);
      }

      const session = await getSession();
      return { data: session ?? undefined };
    }

    return {
      error: result.error?.message || "Invalid credentials",
    };
  } catch (error) {
    console.error("Better Auth sign in error:", error);
    return { error: "Failed to connect to authentication server" };
  }
}

// Sign-up disabled - users are migrated from MstroHub

/**
 * Sign out current user
 */
export async function signOut(): Promise<void> {
  try {
    await betterAuthClient.signOut();
  } catch (error) {
    console.error("Sign out error:", error);
  } finally {
    // Always clear local auth state, even if server signout fails
    _updateCachedToken("");
    // Import and call clearAuthCookies to ensure complete cleanup
    const { clearAuthCookies } = await import("./auth");
    clearAuthCookies();
  }
}

/**
 * Get current session
 */
export async function getSession(): Promise<Session | null> {
  try {
    const result = await betterAuthClient.getSession();
    if (!result.data?.user) {
      return null;
    }

    // Get the session token for API calls
    const sessionToken = result.data?.session?.token;

    // Update cached token if we have one
    if (sessionToken) {
      _updateCachedToken(sessionToken);
    }

    return {
      user: {
        id: result.data.user.id,
        email: result.data.user.email,
        name: result.data.user.name ?? undefined,
        avatar_url: result.data.user.image ?? undefined,
      },
      token: sessionToken,
    };
  } catch {
    return null;
  }
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  const session = await getSession();
  return !!session?.user;
}

/**
 * Get current user from session
 */
export async function getCurrentUser(): Promise<User | null> {
  const session = await getSession();
  return session?.user || null;
}

/**
 * React hook for session
 */
export function useSession() {
  return betterAuthClient.useSession();
}

// Default export for compatibility
const authClientWrapper = {
  signIn,
  signOut,
  getSession,
  isAuthenticated,
  getCurrentUser,
  useSession,
};

export { authClientWrapper as authClient, betterAuthClient };
export default authClientWrapper;
